interface ScreenshotData {
  id: string;
  dataUrl: string;
  timestamp: number;
  type: 'fullPage' | 'viewport';
}

class ScreenshotStorageService {
  private dbName = 'AtarimScreenshotDB';
  private dbVersion = 1;
  private storeName = 'screenshots';
  private db: IDBDatabase | null = null;
  private blobUrls = new Map<string, string>();

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('type', 'type', { unique: false });
        }
      };
    });
  }

  async storeScreenshot(
    id: string,
    dataUrl: string,
    type: 'fullPage' | 'viewport' = 'fullPage'
  ): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);

      const screenshotData: ScreenshotData = {
        id,
        dataUrl,
        timestamp: Date.now(),
        type,
      };

      const request = store.put(screenshotData);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getScreenshot(id: string): Promise<string | null> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(id);

      request.onsuccess = () => {
        const result = request.result as ScreenshotData | undefined;
        resolve(result?.dataUrl || null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getScreenshotBlobUrl(id: string): Promise<string | null> {
    // Check if we already have a blob URL for this screenshot
    if (this.blobUrls.has(id)) {
      return this.blobUrls.get(id)!;
    }

    const dataUrl = await this.getScreenshot(id);
    if (!dataUrl) return null;

    try {
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      this.blobUrls.set(id, blobUrl);
      return blobUrl;
    } catch (error) {
      console.error('Error creating blob URL from screenshot:', error);
      return null;
    }
  }

  async deleteScreenshot(id: string): Promise<void> {
    if (!this.db) await this.init();

    // Clean up blob URL if it exists
    const blobUrl = this.blobUrls.get(id);
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      this.blobUrls.delete(id);
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clearAllScreenshots(): Promise<void> {
    if (!this.db) await this.init();

    // Clean up all blob URLs
    for (const blobUrl of this.blobUrls.values()) {
      URL.revokeObjectURL(blobUrl);
    }
    this.blobUrls.clear();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getLatestScreenshot(
    type?: 'fullPage' | 'viewport'
  ): Promise<ScreenshotData | null> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('timestamp');
      const request = index.openCursor(null, 'prev');

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const screenshot = cursor.value as ScreenshotData;
          if (!type || screenshot.type === type) {
            resolve(screenshot);
          } else {
            cursor.continue();
          }
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Clean up resources
  destroy(): void {
    for (const blobUrl of this.blobUrls.values()) {
      URL.revokeObjectURL(blobUrl);
    }
    this.blobUrls.clear();
    this.db?.close();
  }
}

// Export singleton instance
export const screenshotStorage = new ScreenshotStorageService();
